/** OEM-236 — platform organizations (membership & roles).
 *
 * These hooks own all TanStack Query usage for the organizations feature; the
 * app-shell screen consumes them. react-query lives in `@orkestor/core` (the
 * package that mounts QueryClientProvider), so the query/mutation code lives
 * here too and the screen stays free of a react-query dependency it cannot
 * resolve. Mirrors the shape of `./blocks`. */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useCallback } from "react";

import { useAuth } from "../auth";
import { apiFetch } from "../http";

export type OrganizationType = "empresa" | "personal";
export type OrgRole = "owner" | "admin" | "publisher" | "viewer";

export interface Organization {
  id: string;
  type: OrganizationType;
  name: string;
  created_at: string;
  /** The caller's role in this organization, when known. */
  my_role?: OrgRole | null;
  /** Member count — present in the staff admin listing (`/organizations/all`). */
  member_count?: number | null;
  /** When it was archived, or null/absent while in circulation. Outside the back
      office an archived organization is not listed at all, so this arrives null
      almost everywhere; inside, it is what tells one apart at a glance. */
  archived_at?: string | null;
  /** Los productos contratados.
   *
   *  **`undefined` significa NO CONSTA**, y no es lo mismo que `[]`: la lista
   *  vacía dice «no tiene ninguno» y la ausencia dice «esta respuesta no lo
   *  contó» — una respuesta anterior a que esto existiera, o de un backend más
   *  viejo que el navegador. Pintar «no tienes nada contratado» sobre lo segundo
   *  esconde todos los productos a todo el mundo durante un despliegue. */
  products?: Producto[] | null;
}

export type Producto = "pencil_chief" | "check_me" | "why_humans";

export interface Membership {
  organization_id: string;
  user_id: string;
  role: OrgRole;
  granted_by?: string | null;
  granted_at: string;
  /** Enriched from the directory on the members list (may be absent). */
  email?: string | null;
  name?: string | null;
}

export interface OrganizationDetail extends Organization {
  members: Membership[];
}

interface OrganizationList {
  items: Organization[];
}

/** POST body to create an organization. Only `personal` is offered in the UI;
    `empresa` is staff-only (server returns 403 otherwise). */
export interface CreateOrganizationInput {
  type: OrganizationType;
  name: string;
  /** For empresa: the first admin, by email (resolved server-side) or user id. */
  admin_email?: string;
  admin_user_id?: string;
}

/** POST body to invite/grant a member (owner/admin only). Identify the person by
    exactly one of `email` (the invite path, resolved to an account server-side) or
    `userId` (to change an existing member's role). */
export interface GrantMembershipInput {
  email?: string;
  userId?: string;
  role: OrgRole;
}

const keys = {
  mine: ["platform", "organizations", "mine"] as const,
  all: ["platform", "organizations", "all"] as const,
  staffMe: ["platform", "staff", "me"] as const,
  sysadminMe: ["platform", "sysadmin", "me"] as const,
  detail: (organizationId: string) =>
    ["platform", "organizations", "detail", organizationId] as const,
  invitations: (organizationId: string) =>
    ["platform", "organizations", "invitations", organizationId] as const,
};

/** Resolve the current bearer token once per call. */
function useToken() {
  const { getAccessToken } = useAuth();
  return useCallback(() => getAccessToken(), [getAccessToken]);
}

/** Search organizations by name (for a technician's block-by-name). Disabled
    until `q` has 2+ chars. */
export function useSearchOrganizations(
  q: string,
): UseQueryResult<Organization[], Error> {
  const token = useToken();
  const term = q.trim();
  return useQuery({
    queryKey: ["platform", "organizations", "search", term],
    enabled: term.length >= 2,
    queryFn: async ({ signal }) => {
      const data = await apiFetch<OrganizationList>(
        `/platform/organizations/search?q=${encodeURIComponent(term)}`,
        { token: await token(), signal },
      );
      return data.items;
    },
  });
}

/** The organizations the caller belongs to (with `my_role`). */
export function useMyOrganizations(): UseQueryResult<Organization[], Error> {
  const token = useToken();
  return useQuery({
    queryKey: keys.mine,
    queryFn: async ({ signal }) => {
      const data = await apiFetch<OrganizationList>("/platform/organizations", {
        token: await token(),
        signal,
      });
      return data.items;
    },
  });
}

/** Whether the caller is Orkestor staff — gates the admin console. */
export function useStaffMe(): UseQueryResult<boolean, Error> {
  const token = useToken();
  return useQuery({
    queryKey: keys.staffMe,
    queryFn: async ({ signal }) => {
      const data = await apiFetch<{ is_staff: boolean }>("/platform/staff/me", {
        token: await token(),
        signal,
      });
      return data.is_staff;
    },
  });
}

/** Whether the caller is an Orkestor sysadmin — the tier above staff, which also
    administers the staff list. Gates the status panel: the panel is closed by
    default, so an unanswered or failed check hides it. That means an outage of
    this very API hides the panel from the only people allowed to see it — a
    known cost, recorded in the `health-panel-web` design. */
export function useSysadminMe(): UseQueryResult<boolean, Error> {
  const token = useToken();
  return useQuery({
    queryKey: keys.sysadminMe,
    queryFn: async ({ signal }) => {
      const data = await apiFetch<{ is_sysadmin: boolean }>("/platform/sysadmin/me", {
        token: await token(),
        signal,
      });
      return data.is_sysadmin;
    },
  });
}

/** Every organization on the platform — Orkestor staff admin console only
    (the server returns 403 for non-staff). Each row carries `member_count`. */
export function useAllOrganizations(
  enabled = true,
): UseQueryResult<Organization[], Error> {
  const token = useToken();
  return useQuery({
    queryKey: keys.all,
    enabled,
    queryFn: async ({ signal }) => {
      const data = await apiFetch<OrganizationList>("/platform/organizations/all", {
        token: await token(),
        signal,
      });
      return data.items;
    },
  });
}

/** Create an organization (the UI only submits `type: "personal"`). */
export function useCreateOrganization(): UseMutationResult<
  Organization,
  Error,
  CreateOrganizationInput
> {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ type, name, admin_email, admin_user_id }: CreateOrganizationInput) =>
      apiFetch<Organization>("/platform/organizations", {
        method: "POST",
        token: await token(),
        body: {
          type,
          name,
          ...(admin_email ? { admin_email } : {}),
          ...(admin_user_id ? { admin_user_id } : {}),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.mine });
    },
  });
}

/** Rename an organization (owner/admin). */
export function useRenameOrganization(): UseMutationResult<
  Organization,
  Error,
  { organizationId: string; name: string }
> {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ organizationId, name }) =>
      apiFetch<Organization>(`/platform/organizations/${organizationId}`, {
        method: "PATCH",
        token: await token(),
        body: { name },
      }),
    onSuccess: (_data, { organizationId }) => {
      void qc.invalidateQueries({ queryKey: keys.mine });
      void qc.invalidateQueries({ queryKey: keys.detail(organizationId) });
    },
  });
}

/** Archive or bring an organization back (Orkestor staff).
 *
 * Archiving retires it from circulation without destroying anything, and it can
 * be undone — which is why it sits with staff while deleting sits with sysadmin.
 * Invalidates the back-office listing AND «mine»: an archived organization
 * disappears from its own members' list, so leaving that cache alone would keep
 * showing them a company they can no longer open. */
export function useArchiveOrganization(): UseMutationResult<
  Organization,
  Error,
  { organizationId: string; archived: boolean }
> {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ organizationId, archived }) =>
      apiFetch<Organization>(`/platform/organizations/${organizationId}/archive`, {
        method: archived ? "POST" : "DELETE",
        token: await token(),
      }),
    onSuccess: (_data, { organizationId }) => {
      void qc.invalidateQueries({ queryKey: keys.all });
      void qc.invalidateQueries({ queryKey: keys.mine });
      void qc.invalidateQueries({ queryKey: keys.detail(organizationId) });
    },
  });
}

/** Delete an organization: its owner, or a sysadmin for anyone else's. */
export function useDeleteOrganization(): UseMutationResult<void, Error, string> {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (organizationId: string) =>
      apiFetch<void>(`/platform/organizations/${organizationId}`, {
        method: "DELETE",
        token: await token(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.mine });
      // Y el listado de la trastienda: es desde donde se borra ahora, y dejarlo
      // sin invalidar deja en pantalla la fila de una productora que ya no
      // existe hasta que alguien recargue.
      void qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

/** A single organization with its members. 404 when the caller is not a
    member (existence is not leaked). */
export function useOrganization(
  organizationId: string | null,
): UseQueryResult<OrganizationDetail, Error> {
  const token = useToken();
  return useQuery({
    queryKey: organizationId
      ? keys.detail(organizationId)
      : ["platform", "organizations", "detail", "none"],
    enabled: organizationId != null,
    queryFn: async ({ signal }) =>
      apiFetch<OrganizationDetail>(
        `/platform/organizations/${organizationId}`,
        { token: await token(), signal },
      ),
  });
}

/** A pending invitation to a team organization (Slice B). */
export interface Invitation {
  id: string;
  organization_id: string;
  email: string;
  role: OrgRole;
  invited_by?: string | null;
  status: "pending" | "accepted" | "revoked" | "expired";
  created_at: string;
  expires_at: string;
  /** Present on the invite (202) response: whether the email was delivered now. */
  email_delivered?: boolean | null;
}

/** Granting by email either adds an existing account, or (team org, no account)
    creates a pending invitation that was emailed. */
export type GrantResult =
  | { invited: false; membership: Membership }
  | { invited: true; invitation: Invitation };

/** Grant/invite a member (owner/admin only). An email with no account invites a
    team org (and is refused for a personal one). */
export function useGrantMembership(
  organizationId: string,
): UseMutationResult<GrantResult, Error, GrantMembershipInput> {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ email, userId, role }: GrantMembershipInput): Promise<GrantResult> => {
      const data = await apiFetch<Membership | Invitation>(
        `/platform/organizations/${organizationId}/members`,
        {
          method: "POST",
          token: await token(),
          body: email ? { email, role } : { user_id: userId, role },
        },
      );
      // 200 → Membership (has user_id); 202 → Invitation (has status).
      return "user_id" in data
        ? { invited: false, membership: data }
        : { invited: true, invitation: data };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(organizationId) });
      void qc.invalidateQueries({ queryKey: keys.invitations(organizationId) });
    },
  });
}

/** Revoke a member (owner/admin only; 409 when removing the last owner). */
export function useRevokeMembership(
  organizationId: string,
): UseMutationResult<void, Error, string> {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      apiFetch<void>(
        `/platform/organizations/${organizationId}/members/${userId}`,
        { method: "DELETE", token: await token() },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(organizationId) });
    },
  });
}

/** Pending invitations of an organization (owner/admin only). */
export function useOrganizationInvitations(
  organizationId: string,
): UseQueryResult<Invitation[], Error> {
  const token = useToken();
  return useQuery({
    queryKey: keys.invitations(organizationId),
    queryFn: async ({ signal }) => {
      const data = await apiFetch<{ items: Invitation[] }>(
        `/platform/organizations/${organizationId}/invitations`,
        { token: await token(), signal },
      );
      return data.items;
    },
  });
}

/** Withdraw a pending invitation (owner/admin only). */
export function useRevokeInvitation(
  organizationId: string,
): UseMutationResult<void, Error, string> {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) =>
      apiFetch<void>(
        `/platform/organizations/${organizationId}/invitations/${invitationId}`,
        { method: "DELETE", token: await token() },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.invitations(organizationId) });
    },
  });
}

/** Accept an invitation by its token (the authenticated, email-verified invitee). */
export function useAcceptInvitation(): UseMutationResult<Membership, Error, string> {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationToken: string) =>
      apiFetch<Membership>("/platform/invitations/accept", {
        method: "POST",
        token: await token(),
        body: { token: invitationToken },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.mine });
    },
  });
}
